import { useEffect, useState } from "react";
import { z } from "zod";
import { useLocation } from "wouter";
import { UseFormSetValue } from "react-hook-form";
import { XMarkIcon } from "@heroicons/react/24/outline";
import { Button } from "@/components/ui/button";
import { Loading } from "@/components/fallbacks";
import { TextInput } from "@/components/ui/form/text-input";
import { Form } from "@/components/ui/form/form";
import { useCreateStudy } from "@/api/studio/studies/queries";
import { useGetCategories } from "@/api/manage/categories/queries";
import { updateStudySchema } from "@/api/studio/studies/types/study.type";
import { useModalStore } from "@/stores/use-modal-store";
import { useCaseMapping } from "@/utils/use-case-mapping";
import { useModalError } from "@/hooks/use-modal-error";
import { dynamicUseCase } from "@/utils/dynamic-use-case";
import { useSegment } from "@/hooks/use-segment";
import { cn } from "@/utils/classes";
import selectStudyImg from "@/assets/study-assets/select-study.png";
import selectSceneImg from "@/assets/study-assets/select-scene.png";

type Form = z.infer<typeof updateStudySchema>;
type StudyType = "asset" | "scene" | null;

/* Prop Types */
interface CreateStudyProps {
  projectId: string;
}

/*
 * CreateStudyModal - Modal to create a new study
 */
export const CreateStudyModal = ({ projectId }: CreateStudyProps) => {
  /* Stores */
  const [, navigate] = useLocation();
  const { setConfig } = useModalStore((state) => state.modalActions);

  /* State */
  const [selectedUseCase, setSelectedUseCase] = useState({ id: "", name: "", category: "" });
  const [studyView, setStudyView] = useState<"selectType" | "useCase" | "title">("selectType");
  const [studyType, setStudyType] = useState<StudyType>(null);

  /* Queries */
  const { mutateAsync: createStudy, isPending: isCreateStudyPending, isError: isCreateStudyError } = useCreateStudy();
  const { data: categories, isLoading: isCategoriesLoading, isError: isCategoriesError } = useGetCategories(studyType || undefined);

  /* Hooks */
  const { close } = useModalError({ error: isCreateStudyError });
  const { segmentTrackEvent } = useSegment();

  /* Handlers */
  const handleCreate = async (values: Form) => {
    // Default to "asset" if no study type is selected
    const type = studyType || "asset";

    const resp = await createStudy({
      projectId,
      values: {
        ...values,
        type,
      },
    });

    // Segment Tracking
    segmentTrackEvent("Study Created", {
      projectId: projectId,
      studyId: resp.study.id,
      studyName: resp.study.name,
      studyType: type,
      // we have to get the useCase info from state as it is not provided in the response
      useCaseId: selectedUseCase.id,
      useCaseName: selectedUseCase.name,
      useCaseCategory: selectedUseCase.category,
    });

    // Navigate to the next view
    navigate(`/projects/${projectId}/studies/${resp.study.id}/${type}/study-select`);
    close();
  };

  const handleUsecaseSelect = (useCase: { id: string; name: string; category: string }, setValue: UseFormSetValue<Form>) => {
    setSelectedUseCase(useCase);

    // Set values for hidden inputs on the next view
    setValue("name", useCase.name);
    setValue("useCaseId", useCase.id);
    setValue("useCaseName", useCase.name);
  };

  /* Side Effects */
  useEffect(() => {
    // Set modal size based on view
    if (studyView === "selectType") {
      setConfig({ closeOnClickOutside: false, className: "w-full max-w-[650px]" });
    } else if (studyView === "useCase") {
      setConfig({ closeOnClickOutside: false, className: "w-[60vw] max-w-[830px] max-h-[70vh]" });
    } else {
      setConfig({ closeOnClickOutside: false, className: "" });
    }
  }, [studyView, setConfig]);

  /* Render */
  return (
    <Form schema={updateStudySchema} onSubmit={handleCreate}>
      {({ register, formState: { errors }, setValue }) => {
        // Study type selection view
        if (studyView === "selectType") {
          return (
            <>
              <div className="flex items-center justify-between pb-6">
                <h2>What are you testing?</h2>
                <XMarkIcon onClick={close} className="size-5 cursor-pointer" />
              </div>

              <div className="grid grid-cols-2 gap-4">
                {/* Assets Card */}
                <div
                  className={cn("cursor-pointer rounded-2xl p-6 border-2 transition-all", {
                    "bg-primary border-primary text-white": studyType === "asset",
                    "border-base-300 hover:border-base-400": studyType !== "asset",
                  })}
                  onClick={() => setStudyType("asset")}
                >
                  <div className="w-full mb-4 bg-base-200 rounded-xl">
                    <img src={selectStudyImg} alt="Assets" className="w-full h-auto object-contain" />
                  </div>
                  <h3 className="font-semibold mb-2">Assets</h3>
                  <ul
                    className={cn("space-y-2 text-sm list-disc list-outside pl-4", {
                      "opacity-60": studyType !== "asset",
                    })}
                  >
                    <li className="pl-1">Upload standalone assets like hero images, display ads, packaging, product pack.</li>
                    <li className="pl-1">Optionally add contextual environment.</li>
                  </ul>
                </div>

                {/* Scenes Card */}
                <div
                  className={cn("cursor-pointer rounded-2xl p-6 border-2 transition-all", {
                    "bg-[#9747FF] border-[#9747FF] text-white": studyType === "scene",
                    "border-base-300 hover:border-base-400": studyType !== "scene",
                  })}
                  onClick={() => setStudyType("scene")}
                >
                  <div className="w-full mb-4 bg-base-200 rounded-xl">
                    <img src={selectSceneImg} alt="Scenes" className="w-full h-auto object-contain" />
                  </div>
                  <h3 className="font-semibold mb-2">Scenes</h3>
                  <ul
                    className={cn("space-y-2 text-sm list-disc list-outside pl-4", {
                      "opacity-60": studyType !== "scene",
                    })}
                  >
                    <li className="pl-1">Upload images with assets already placed in a contextual environment like a product shelf or online store.</li>
                  </ul>
                </div>
              </div>

              {/* Nav buttons */}
              <div className="sticky bottom-0 flex justify-end bg-base-100 py-4 translate-y-6">
                <Button variant="primary" disabled={!studyType} onClick={() => setStudyView("useCase")}>
                  Continue
                </Button>
              </div>
            </>
          );
        }

        // Use case selection view
        if (studyView === "useCase") {
          return (
            <>
              <div className="flex items-center justify-between pb-6">
                <h2>{studyType === "scene" ? "Which scene you placed your assets in?" : "Choose a study type"}</h2>
                <XMarkIcon onClick={close} className="size-5 cursor-pointer" />
              </div>
              {isCategoriesLoading && <Loading />}
              {isCategoriesError && (
                <div className="flex justify-center">
                  <p>Failed to load categories</p>
                </div>
              )}
              {categories && (
                <div>
                  {categories.map((category) => (
                    <div key={category.id} className="pb-6 grid grid-cols-3 gap-4">
                      <h3 className="opacity-60 pb-2 col-span-3">{category.name}</h3>
                      {category.useCases.map((useCase) => (
                        <div
                          key={useCase.id}
                          className={`border cursor-pointer border-base-300 rounded-2xl p-4 ${selectedUseCase.id === useCase.id ? "bg-" + useCaseMapping[useCase.name]?.color : null}`}
                          onClick={() => handleUsecaseSelect({ id: useCase.id, name: useCase.name, category: useCase.category }, setValue)}
                        >
                          {dynamicUseCase(useCase.name, `size-6 ${selectedUseCase.id === useCase.id ? "text-base-100" : "text-" + useCaseMapping[useCase.name]?.color}`)}
                          <h3 className={`pt-4 ${selectedUseCase.id === useCase.id ? "text-base-100" : ""}`}>{useCase.name}</h3>
                        </div>
                      ))}
                    </div>
                  ))}
                </div>
              )}
              {/* Nav buttons */}
              <div className="sticky bottom-0 flex justify-end bg-base-100 py-4 translate-y-6">
                <Button type="button" className="mr-2" onClick={() => setStudyView("selectType")}>
                  Back
                </Button>
                <Button variant="primary" disabled={!selectedUseCase.id && !selectedUseCase.name} onClick={() => setStudyView("title")}>
                  Continue
                </Button>
              </div>
            </>
          );
        }

        // Title confirmation view
        return (
          <>
            <div className="flex items-center justify-between pb-6">
              <h2>Confirm study title</h2>
              <XMarkIcon onClick={close} className="size-5 cursor-pointer" />
            </div>

            {/* Study title input */}
            <TextInput label="Study title" registration={register("name")} error={errors.name?.message} placeholder="Enter a name" />

            {/* Hidden inputs to save useCases from the previous step */}
            <TextInput type="hidden" registration={register("useCaseId")} error={errors.useCaseId?.message} />
            <TextInput type="hidden" registration={register("useCaseName")} error={errors.useCaseName?.message} />

            {/* Hard code the first step of the study flow */}
            <TextInput type="hidden" registration={register("route")} value="study-select" />

            {/* Nav buttons */}
            <div className="sticky bottom-0 flex justify-end bg-base-100 py-4 translate-y-6">
              <Button type="button" className="mr-2" onClick={() => setStudyView("useCase")}>
                Back
              </Button>
              <Button type="submit" variant="primary" disabled={isCreateStudyPending}>
                {isCreateStudyPending ? "Creating..." : "Create study"}
              </Button>
            </div>
          </>
        );
      }}
    </Form>
  );
};
